import { Injectable } from "@angular/core";
import { PreloadingStrategy, Route } from "@angular/router";
import { Observable, of } from "rxjs";
import { catchError, delay, mergeMap } from "rxjs/operators";

/**
 * Custom preloading strategy that selectively preloads modules based on data properties
 */
@Injectable({
	providedIn: "root",
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
	/**
	 * Selectively preloads routes based on the `preload` property set in the route data
	 * Priority values in the preload property determine delay:
	 * - high: Preload immediately (0ms delay)
	 * - medium: Preload after short delay (1000ms)
	 * - low: Preload after longer delay (3000ms)
	 */
	preload(route: Route, load: () => Observable<any>): Observable<any> {
		// Only preload if the route has indicated it should be preloaded
		if (route.data && route.data["preload"]) {
			// Determine the delay based on priority - increased delays to prevent race conditions
			let delayTime = 0;

			if (typeof route.data["preload"] === "string") {
				switch (route.data["preload"]) {
					case "high":
						delayTime = 500; // Small delay even for high priority
						break;
					case "medium":
						delayTime = 2000; // Increased delay
						break;
					case "low":
						delayTime = 5000; // Longer delay
						break;
					default:
						return of(null); // Don't preload if priority is not recognized
				}
			}

			console.log(
				`Preloading: ${route.path} with priority ${route.data["preload"]}`,
			);

			// Preload with appropriate delay and error handling
			return of(true).pipe(
				delay(delayTime),
				mergeMap(() => load()),
				catchError((error) => {
					console.warn(`Failed to preload ${route.path}:`, error);
					return of(null); // Don't fail the entire preloading process
				}),
			);
		}

		// Don't preload if not specified
		return of(null);
	}
}

/**
 * Network-aware preloading strategy that only preloads when the network is not limited
 */
@Injectable({
	providedIn: "root",
})
export class NetworkAwarePreloadStrategy implements PreloadingStrategy {
	preload(route: Route, load: () => Observable<any>): Observable<any> {
		// Check if route is marked for preloading
		if (route.data && route.data["preload"]) {
			// Check connection quality
			const connection = (navigator as any).connection;

			if (!connection) {
				// If network info isn't available, only preload high priority routes
				if (route.data["preload"] === "high") {
					return load();
				}
				return of(null);
			}

			// Check if we're on a slow connection or if data saver is enabled
			if (
				connection.saveData ||
				connection.effectiveType === "slow-2g" ||
				connection.effectiveType === "2g"
			) {
				// Don't preload anything on slow connections or when data saver is enabled
				return of(null);
			}

			// On 3G, only preload high priority
			if (connection.effectiveType === "3g" && route.data["preload"] !== "high") {
				return of(null);
			}

			// On good connections, preload according to priority
			return load();
		}

		// Don't preload routes not marked for preloading
		return of(null);
	}
}
